import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CreditCard, Download, ExternalLink, Loader2, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useOrg } from "@/hooks/useOrg";
import { API, api } from "@/lib/api";

const STATUS_NOTICE = {
  grace: {
    cls: "border-[color:rgba(212,175,55,0.4)] bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]",
    text: "There was a problem with your last payment. Your team keeps full access, but new invites and new approvals are paused until it's resolved.",
  },
  readonly: {
    cls: "border-[color:rgba(248,113,113,0.4)] bg-[color:rgba(248,113,113,0.08)] text-[color:#f87171]",
    text: "This org is read-only — the plan lapsed or was cancelled. Nothing new can be submitted and the registry no longer serves your team. Export your data below, or upgrade to restore access.",
  },
  suspended: {
    cls: "border-[color:rgba(248,113,113,0.4)] bg-[color:rgba(248,113,113,0.08)] text-[color:#f87171]",
    text: "This org has been suspended.",
  },
};

export default function OrgBilling() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const [billing, setBilling] = useState(null);
  const [seatsInput, setSeatsInput] = useState("");
  const [busy, setBusy] = useState(false);
  const isOwner = org && org.role === "owner";

  const load = useCallback(async () => {
    if (!slug || !isOwner) return;
    try {
      const { data } = await api.get(`/orgs/${slug}/billing`);
      setBilling(data);
      setSeatsInput(String(data.seats));
    } catch (e) {
      setBilling(false);
    }
  }, [slug, isOwner]);

  useEffect(() => {
    load();
  }, [load]);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${slug}/billing/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const updateSeats = async () => {
    const seats = parseInt(seatsInput, 10);
    if (!seats || seats < 1) {
      toast.error("Enter a valid seat count");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/orgs/${slug}/billing/seats`, { seats });
      toast.success("Seat change requested — it'll reflect here once Stripe confirms.");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not update seats");
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.get(`/orgs/${slug}/billing/portal`, { params: { origin_url: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Billing portal is unavailable");
    }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/orgs/${slug}/export`, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setBusy(false);
    }
  };

  if (org && !isOwner) {
    return (
      <AppShell>
        <OrgTabs slug={slug} />
        <div className="mt-8 text-center text-sm text-[color:var(--rb-text-faint)]">Owner access required.</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
          Billing{org ? ` — ${org.name}` : ""}
        </h1>
      </div>

      {billing === null ? (
        <div className="mt-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[color:var(--rb-text-faint)]" /></div>
      ) : billing === false ? (
        <div className="mt-8 text-center text-sm text-[color:var(--rb-text-faint)]">Could not load billing.</div>
      ) : (
        <div className="mt-6 max-w-xl space-y-4">
          {billing.status !== "active" && STATUS_NOTICE[billing.status] && (
            <div className={`flex items-start gap-2 rounded-2xl border p-3 text-xs ${STATUS_NOTICE[billing.status].cls}`} data-testid="billing-status-notice">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{STATUS_NOTICE[billing.status].text}</p>
            </div>
          )}

          <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-[color:var(--rb-text)]">
                  {billing.plan === "team" ? "Team plan" : "Free plan"}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-[color:var(--rb-text-faint)]">
                  <Users className="h-3.5 w-3.5" /> {billing.seats_in_use} of {billing.seats} seats in use
                </div>
              </div>
              {billing.plan === "free" && (
                <Button onClick={upgrade} disabled={busy || !billing.checkout_available} data-testid="upgrade-team-button"
                  className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)] disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Upgrade — $${(billing.team_price_cents / 100).toFixed(0)}/seat/mo`}
                </Button>
              )}
            </div>
            {billing.plan === "free" && !billing.checkout_available && (
              <p className="mt-2 text-[11px] text-[color:var(--rb-text-faint)]">Team billing isn't configured on this deployment yet.</p>
            )}

            {billing.plan === "team" && (
              <div className="mt-4 flex items-center gap-2 border-t border-[color:var(--rb-border)] pt-4">
                <Input type="number" min="1" value={seatsInput} onChange={(e) => setSeatsInput(e.target.value)} data-testid="seats-input"
                  className="h-9 w-24 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]" />
                <Button size="sm" variant="outline" onClick={updateSeats} disabled={busy} data-testid="update-seats-button"
                  className="rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)]">
                  Update seats
                </Button>
                {billing.portal_available && (
                  <Button size="sm" variant="ghost" onClick={openPortal} data-testid="billing-portal-button"
                    className="ml-auto gap-1 text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
                    Manage billing & invoices <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
            <div className="text-sm font-medium text-[color:var(--rb-text)]">Export your data</div>
            <p className="mt-1 text-xs text-[color:var(--rb-text-faint)]">
              Every published artifact in this org's registry, zipped. Cancelling never holds your team's content hostage.
            </p>
            <Button size="sm" variant="outline" onClick={exportData} disabled={busy} data-testid="export-org-button"
              className="mt-3 gap-1.5 rounded-xl border-[color:var(--rb-border)] text-[color:var(--rb-text)]">
              <Download className="h-3.5 w-3.5" /> Export .zip
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
